'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { CompactTable, TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { flexRender } from '@tanstack/react-table';
import type { DashboardBorrowPositionsFilters } from '@/helpers/filter/table';
import type { UserBorrowPosition, UserBorrowPositionFacets } from '@/hooks/positions/userBorrowPositionsQuery';
import { InternalLink } from '@/components/common/InternalLink';
import { getMarketUrl } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import type { BorrowPositionsTableFeatures } from '../config/BorrowPositionsTable.columns';
import type { BorrowPositionsTableRow } from '../config/BorrowPositionsTable.types';
import DashboardPositionsTable, { DATA_CELL_CONTENT_CLASS, PINNED_CELL_CONTENT_CLASS, POSITION_MANAGER_COLUMN_ID } from '../../DashboardPositionsTable';
import DashboardBorrowPositionsFiltersComponent from './DashboardBorrowPositionsFilters';
interface DashboardBorrowPositionsDesktopViewProps {
  table: ReactTable<BorrowPositionsTableFeatures, BorrowPositionsTableRow>;
  filters?: DashboardBorrowPositionsFilters;
  onFiltersChange?: (filters: DashboardBorrowPositionsFilters) => void;
  positionChainIds?: ChainId[];
  columnLabels: Record<string, string>;
  borrowPositions: UserBorrowPosition[];
  loading: boolean;
  disabled?: boolean;
  facets?: UserBorrowPositionFacets;
}
const DashboardBorrowPositionsDesktopView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  columnLabels,
  borrowPositions,
  loading,
  disabled,
  facets
}: DashboardBorrowPositionsDesktopViewProps) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const dataRows = table.getRowModel().rows;
  const hasFilteredResults = dataRows.length > 0;
  return <div data-testid="dashboard-borrow-positions-card" className="border-border bg-card flex min-h-0 flex-col rounded-[20px] border-[0.5px] shadow-md dark:shadow-none">
      <div className="bg-card sticky z-1 overflow-hidden rounded-t-[20px]">
        <div className="p-md box-border content-center rounded-t-[20px]">
          <DashboardBorrowPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
        </div>
      </div>

      {!hasFilteredResults ? <TableNoFilterResults withBorder withBackground={false} type="markets" count={borrowPositions.length} clearFilters={() => onFiltersChange?.({
      collateralAssetIdsFilter: [],
      loanAssetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <DashboardPositionsTable columns={table.getVisibleLeafColumns()} data-testid="dashboard-borrow-positions-table">
          <CompactTable.Head headerGroups={table.getHeaderGroups()} columnLabels={columnLabels} />
          <CompactTable.Body className="[&>tr:last-child_div]:border-b-0">
            {dataRows.map(row => {
          const collateralSymbol = row.original.market.collateralAsset?.symbol;
          const marketUrl = collateralSymbol ? getMarketUrl({
            identifier: InterfaceChainUtils.getIdentifierById(row.original.market.chainId),
            marketId: row.original.market.marketId,
            collateralSymbol,
            loanSymbol: row.original.market.loanAsset.symbol,
            hash: isSelf ? 'yourPosition' : undefined
          }) : undefined;
          return <CompactTable.Row key={row.id} data-testid={`market-${row.original.market.marketId}`}>
                  {row.getVisibleCells().map(cell => {
              const content = <CompactTable.CellContent className={cell.column.id === POSITION_MANAGER_COLUMN_ID ? PINNED_CELL_CONTENT_CLASS : DATA_CELL_CONTENT_CLASS}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </CompactTable.CellContent>;
              return <CompactTable.Cell key={cell.id}>
                        {marketUrl ? <InternalLink href={marketUrl} prefetch="on-hover-debounced">
                            {content}
                          </InternalLink> : content}
                      </CompactTable.Cell>;
            })}
                </CompactTable.Row>;
        })}
          </CompactTable.Body>
        </DashboardPositionsTable>}
    </div>;
};
export default DashboardBorrowPositionsDesktopView;